import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import { userService } from "../../services";
import { Container } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

const PREFIX = "PaymentsDashboard";

const classes = {
  marginBottom: `${PREFIX}-marginBottom`,
  filter: `${PREFIX}-filter`,
  mobileMarginBottom: `${PREFIX}-mobileMarginBottom`,
  exparienceLogo: `${PREFIX}-exparienceLogo`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.marginBottom}`]: {
    marginBottom: "0.5rem",
  },

  [`& .${classes.filter}`]: {
    display: "flex",
    justifyContent: "space-around",
  },

  [`&.${classes.mobileMarginBottom}`]: {
    [theme.breakpoints.between("xs", "md")]: {
      marginBottom: "5rem",
    },
  },

  [`& .${classes.exparienceLogo}`]: {
    width: "50px",
    height: "50px",
  },
}));

export const PaymentsDashboard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [payments, setPayments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [rowCount, setRowCount] = React.useState(0);
  const [sortModel, setSortModel] = React.useState({
    field: "createdDate",
    sort: "desc",
  });

  const columns = [
    {
      field: "expLogo",
      headerName: "Logo",
      width: 120,
      renderCell: (params) => (
        <img
          className={classes.exparienceLogo}
          src={params.row.exparience.logoUrl}
        />
      ),
    },
    {
      field: "exparience",
      headerName: "Exparience",
      width: 160,
      valueGetter: (params) => params.row.exparience.name,
    },
    {
      field: "amount",
      headerName: t("paymentsDashboard.amount"),
      width: 125,
      valueGetter: (params) => {
        if (params.row.productIdentifier)
          return `${
            parseInt(params.row.productIdentifier.split("_")[1]) / 100
          }€`;
        else return t("paymentsDashboard.noData");
      },
    },
    {
      field: "originalPuchaseDate",
      headerName: t("paymentsDashboard.buyDate"),
      width: 160,
      valueGetter: (params) => {
        if (params.row.originalPurchaseDate) {
          const tmp = params.row.originalPurchaseDate.split("T");
          const time = tmp[1].slice(0, 5).replace(":", "h");
          return `${tmp[0]} - ${time}`;
        } else return t("paymentsDashboard.noData");
      },
    },
    { field: "store", headerName: t("paymentsDashboard.store"), width: 120 },
    {
      field: "User",
      headerName: t("paymentsDashboard.user"),
      width: 120,
      valueGetter: (params) => {
        if (params.row.revenueCatId) {
          if (params.row.revenueCatId.includes("Anonymous"))
            return t("paymentsDashboard.anonymous");
          else {
            const tmp = params.row.revenueCatId.split("_");
            let final = "";
            tmp.forEach((item, index) => {
              if (index > 1) final += item;
            });
            return final;
          }
        } else return t("paymentsDashboard.noData");
      },
    },
  ];

  const handlePageChange = (page) => {
    setPage(page + 1);
  };

  const handleSortModelChange = (params) => {
    if (params !== sortModel) {
      setSortModel(params[0]);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, sortModel]);

  const loadData = () => {
    setLoading(true);
    userService
      .getPayments(page - 1, 10)
      .then((result) => {
        setRowCount(() => result.total);
        setPayments(() => result.data);
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      })
      .finally(() => setLoading(false));
  };

  return (
    <StyledDiv>
      <Container className={classes.mobileMarginBottom}>
        <h1>{t("paymentsDashboard.title")}</h1>
        <DataGrid
          rows={payments}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          pagination
          paginationMode="server"
          rowCount={rowCount}
          loading={loading}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          onPaginationModelChange={(model, details) =>
            handlePageChange(model.page)
          }
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10]}
        />
      </Container>
    </StyledDiv>
  );
};
